import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PortalWords, { wordsText } from './PortalWords';
import PortalVenue from './PortalVenue';
import PortalEvent from './PortalEvent';
import ringArt from '../../../assets/img/portal-ring.png';
import { safePad } from '../../../utils/safeArea';
import { TicketIcon, PinIcon, ClockIcon, ScissorsIcon, ResetIcon, RotateIcon, HighlightIcon, SearchIcon, TrashIcon, LockIcon, EyeIcon } from './icons';
import {
  FONTS, HIGHLIGHTS, ELEMENT_STYLES, BACKGROUNDS,
  SIZE_MIN, SIZE_MAX, SIZE_DEFAULT, TEXT_MAX, TEXT_DEFAULT, EVENT_DEFAULT, VENUE_DEFAULT, SAFE_TOP, TOP_LIMIT,
  ELEMENT_SCALE_MIN, ELEMENT_SCALE_MAX, MEDIA_SCALE_MIN, MEDIA_SCALE_MAX, ROT_DEFAULT, SCL_DEFAULT,
  highlightOf, backgroundOf, cycle, snapRotation,
} from '../../../utils/portalLayout';
import { FRAME_DEFAULT, clampFrame } from '../../../utils/mediaFrame';
import useKeyboardInset from '../../../utils/useKeyboardInset';

/**
 * The interactive layer over a Portal card (handoff v3 §5): the words are typed on the Portal
 * itself, and every element — the words, the event, the venue, and in photo mode the picture —
 * moves with one finger, sizes and turns with two (or a wheel and a rotate handle on a desktop).
 * The right rail links an event, a venue and a window and opens the trim sheet; a tapped event
 * or venue gets its style bar in the bottom safe zone; a text Portal picks its background from
 * the swatch strip or by swiping the card.
 *
 * It is an absolutely positioned overlay the same size as the card, so every percent it
 * computes is a percent of the card. The bottom 13% is the guests' action row: nothing can be
 * dragged into it, and after any change that grows an element it is pushed back up (keepSafe).
 *
 * `editing` (typing) and `selected` (an event or venue with its style bar showing) are the
 * parent's, because the composer's header and NEXT hide while typing. `locked` draws the
 * elements without any gestures, except a tap on the words (`onTapText`).
 */
const TAP_PX = 6;
const SWIPE_PX = 40;
const SIZE_RANGE = SIZE_MAX - SIZE_MIN;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const RAIL_ALL = ['text', 'event', 'venue', 'window', 'trim', 'reset'];
const label9 = { fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textShadow: '0 1px 3px rgba(0,0,0,.9)' };

const circle = (on) => ({
  width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  background: on ? '#fff' : 'rgba(0,0,0,.72)', color: on ? '#000' : '#fff', border: 0, borderRadius: '50%', cursor: 'pointer',
});

/** The white rotate knob on a stem, 34px above the element's top centre. */
function RotateHandle({ onPointerDown }) {
  return (
    <>
      <span
        role="button"
        tabIndex={-1}
        aria-label="Rotate"
        title="Drag to rotate"
        data-testid="rotate-handle"
        onPointerDown={onPointerDown}
        style={{ position: 'absolute', left: '50%', top: -34, width: 28, height: 28, marginLeft: -14, borderRadius: '50%', background: '#fff', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', touchAction: 'none', boxShadow: '0 1px 6px rgba(0,0,0,.6)', zIndex: 2 }}
      >
        <RotateIcon />
      </span>
      <span aria-hidden style={{ position: 'absolute', left: '50%', top: -8, width: 2, height: 8, marginLeft: -1, background: '#fff' }} />
    </>
  );
}

/** The BACKGROUND strip: 40px round swatches, the party ones badged (handoff §5.6). */
export function BackgroundSwatches({ value, onChange, rowRef = null }) {
  const localRef = useRef(null);
  const ref = rowRef || localRef;
  const idx = BACKGROUNDS.findIndex((b) => b.key === value);
  const pick = (key) => {
    onChange(key);
    const row = ref.current;
    const i = BACKGROUNDS.findIndex((b) => b.key === key);
    if (row?.children[i]) row.scrollTo?.({ left: Math.max(0, row.children[i].offsetLeft - row.clientWidth / 2 + 20), behavior: 'smooth' });
  };
  useEffect(() => {
    const row = ref.current;
    const i = BACKGROUNDS.findIndex((b) => b.key === value);
    if (row?.children[i]) row.scrollLeft = Math.max(0, row.children[i].offsetLeft - row.clientWidth / 2 + 20);
    // Centred once, when the strip appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="flex flex-col font-mono text-white" style={{ gap: 10 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em' }}>
        BACKGROUND · {backgroundOf(value).label} <span style={{ color: 'rgba(255,255,255,.6)' }}>· {idx + 1}/{BACKGROUNDS.length} · SWIPE ↔</span>
      </span>
      <div ref={ref} className="prc-row flex" style={{ gap: 10, overflowX: 'auto', padding: 4 }} data-testid="bg-swatches">
        {BACKGROUNDS.map((b) => {
          const on = b.key === value;
          return (
            <button
              key={b.key}
              type="button"
              aria-label={b.label}
              aria-pressed={on}
              title={b.label}
              onClick={() => pick(b.key)}
              style={{ position: 'relative', flex: '0 0 40px', width: 40, height: 40, padding: 0, background: b.swatch, border: 0, borderRadius: '50%', outline: on ? '2px solid #00FFFF' : '2px solid transparent', outlineOffset: 3, cursor: 'pointer' }}
            >
              {b.party && (
                <span aria-hidden data-testid="party-badge" style={{ position: 'absolute', right: -3, top: -3, width: 12, height: 12, borderRadius: '50%', background: '#fff', boxShadow: '0 0 0 2px #000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#FF33C6' }} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const VENUE_OPEN_ARIA = {
  locked: 'Place hidden until they’re in. Tap to show right away',
  open: 'Place shown to everyone. Tap to hide until they’re in',
};

/**
 * `{EVENT|VENUE} · HOW IT SHOWS`: the eleven style pills, then for the venue the lock / eye that
 * flips the venue lock (venue-lock handoff §3.3), then change and remove.
 */
export function ElementStyleBar({ kind, value, onChange, onSearch, onRemove, venueOpen = false, onVenueOpen = null }) {
  return (
    <div className="flex flex-col font-mono text-white" style={{ gap: 8 }} data-testid="style-bar">
      <span style={{ paddingLeft: 4, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em' }}>{kind.toUpperCase()} · HOW IT SHOWS</span>
      <div className="flex items-center" style={{ gap: 6 }}>
        <div className="prc-row flex" style={{ flex: 1, minWidth: 0, gap: 6, overflowX: 'auto' }}>
          {ELEMENT_STYLES.map((s) => {
            const on = s.key === value;
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={on}
                onClick={() => onChange(s.key)}
                className="prc-pill"
                style={{ flex: '0 0 auto', padding: '0 14px', whiteSpace: 'nowrap', height: 40, background: on ? '#fff' : 'rgba(0,0,0,.72)', color: on ? '#000' : '#fff', borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em' }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        {kind === 'venue' && onVenueOpen && (
          <button
            type="button"
            aria-label={venueOpen ? VENUE_OPEN_ARIA.open : VENUE_OPEN_ARIA.locked}
            title={venueOpen ? VENUE_OPEN_ARIA.open : VENUE_OPEN_ARIA.locked}
            aria-pressed={venueOpen}
            data-testid="venue-open-toggle"
            onClick={() => onVenueOpen(!venueOpen)}
            style={{ ...circle(venueOpen), width: 40, height: 40, flexShrink: 0, transition: 'background-color .2s, color .2s' }}
          >
            {venueOpen ? <EyeIcon /> : <LockIcon size={16} />}
          </button>
        )}
        <button type="button" aria-label="Change" onClick={onSearch} className="prc-pill" style={{ ...circle(false), width: 40, height: 40, flexShrink: 0 }}><SearchIcon /></button>
        <button type="button" aria-label="Remove" onClick={onRemove} className="prc-pill" style={{ ...circle(false), width: 40, height: 40, flexShrink: 0 }}><TrashIcon /></button>
      </div>
    </div>
  );
}

export default function PortalLayoutEditor({
  text = '',
  onText = null,
  chip = '',
  layout,
  onLayout,
  scale = 1,
  textOnly = false,
  event = null,
  venue = null,
  venueDate = null,
  frame = null,
  onFrame = null,
  isVideo = false,
  windowOn = false,
  editing = false,
  onEditing = null,
  selected = null,
  onSelected = null,
  onOpen = null,
  onRemove = null,
  rail = RAIL_ALL,
  locked = false,
  onTapText = null,
  railTop = 100,
  showRail = true,
  showSwatches = true,
  showStyleBar = true,
  showChrome = true,
  venueOpen = false,        // the venue lock: true shows the place to everyone
  onVenueOpen = null,
  venueTip = false,         // the first-time tooltip on the venue (the parent owns "seen")
  onVenueTip = null,        // (false) when it is dismissed
  mode = 'open',            // open | curated — the tooltip's copy follows it
}) {
  const boxRef = useRef(null);
  const tipRef = useRef(null);
  const wordsRef = useRef(null);
  const spanRef = useRef(null);
  const eventRef = useRef(null);
  const venueRef = useRef(null);
  const rowRef = useRef(null);
  const [dragging, setDragging] = useState(null);   // 'text' | 'event' | 'venue' | 'media' | 'size' | null
  const [panned, setPanned] = useState(false);

  // On a phone this overlay IS the screen, so the font row hanging off its bottom edge is the
  // first thing the keyboard covers — and the keyboard is raised by the same tap that shows it.
  // Ride above it instead, by however much of the CARD the keyboard is actually over: 0 on a
  // desktop frame, and 0 in the native shell, which shrinks its own WebView.
  const kbInset = useKeyboardInset(editing, boxRef);

  // The gesture engine reads the newest values from refs: its listeners live on window.
  const layoutRef = useRef(layout); layoutRef.current = layout;
  const frameRef = useRef(frame); frameRef.current = frame;
  const editingRef = useRef(editing); editingRef.current = editing;
  const selectedRef = useRef(selected); selectedRef.current = selected;
  const ptrs = useRef(new Map());
  const g = useRef(null);
  const pinch = useRef(null);

  const set = useCallback((patch) => onLayout({ ...layoutRef.current, ...patch }), [onLayout]);
  const cardRect = () => boxRef.current?.getBoundingClientRect();
  const elOf = (key) => (key === 'text' ? wordsRef.current : key === 'event' ? eventRef.current : venueRef.current);
  const rotOf = (key) => layoutRef.current.rot?.[key] || 0;
  const sclOf = (key) => layoutRef.current.scl?.[key] || 1;
  const mediaScale = () => (frameRef.current?.scale || 100) / 100;

  // ---- typing ----
  const done = useCallback(() => {
    const el = spanRef.current;
    const t = (el ? wordsText(el) : text).replace(/\n+$/, '').trim();
    if (el) el.textContent = t;
    try { window.getSelection()?.removeAllRanges(); } catch { /* none */ }
    onText?.(t);
    onEditing?.(false);
  }, [text, onText, onEditing]);

  const edit = useCallback(() => {
    if (locked) { onTapText?.(); return; }
    onSelected?.(null);
    onEditing?.(true);
  }, [locked, onTapText, onSelected, onEditing]);

  // ---- the gesture engine ----
  const startPinch = useCallback((key) => {
    const [a, b] = [...ptrs.current.values()];
    pinch.current = {
      key,
      d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      a0: Math.atan2(b.y - a.y, b.x - a.x),
      s0: key === 'text' ? layoutRef.current.size : key === 'media' ? mediaScale() : sclOf(key),
      r0: rotOf(key),
    };
    if (g.current) g.current.moved = true;
    setDragging(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const down = (key) => (e) => {
    if (e.button != null && e.button !== 0) return;
    if (key === 'text' && editingRef.current) return;
    if (locked && key !== 'text') return;
    e.preventDefault();
    e.stopPropagation();
    ptrs.current.set(e.pointerId ?? 1, { x: e.clientX, y: e.clientY });
    if (ptrs.current.size === 2 && !locked) { startPinch(g.current ? g.current.key : key); return; }
    if (ptrs.current.size > 1) return;
    const p0 = key === 'media' ? clampFrame(frameRef.current || FRAME_DEFAULT) : layoutRef.current[key];
    g.current = { key, el: e.currentTarget, sx: e.clientX, sy: e.clientY, p0, moved: false };
  };

  useEffect(() => {
    const onWinDown = (e) => {
      // A second finger anywhere on the page upgrades the drag to a pinch.
      const id = e.pointerId ?? 1;
      if (!g.current || locked || ptrs.current.has(id) || ptrs.current.size !== 1) return;
      ptrs.current.set(id, { x: e.clientX, y: e.clientY });
      startPinch(g.current.key);
    };
    const onMove = (ev) => {
      const id = ev.pointerId ?? 1;
      if (!ptrs.current.has(id)) return;
      ptrs.current.set(id, { x: ev.clientX, y: ev.clientY });
      const L = layoutRef.current;
      if (pinch.current && ptrs.current.size >= 2) {
        const [a, b] = [...ptrs.current.values()];
        const p = pinch.current;
        const ratio = Math.hypot(a.x - b.x, a.y - b.y) / p.d0;
        const r = snapRotation(p.r0 + ((Math.atan2(b.y - a.y, b.x - a.x) - p.a0) * 180) / Math.PI);
        if (p.key === 'text') {
          onLayout({ ...L, size: Math.round(clamp(p.s0 * ratio, SIZE_MIN, SIZE_MAX)), rot: { ...L.rot, text: r } });
        } else if (p.key === 'media') {
          onFrame?.(clampFrame({ ...(frameRef.current || FRAME_DEFAULT), scale: clamp(p.s0 * ratio, MEDIA_SCALE_MIN, MEDIA_SCALE_MAX) * 100 }));
          onLayout({ ...L, rot: { ...L.rot, media: r } });
        } else {
          onLayout({ ...L, scl: { ...L.scl, [p.key]: clamp(p.s0 * ratio, ELEMENT_SCALE_MIN, ELEMENT_SCALE_MAX) }, rot: { ...L.rot, [p.key]: r } });
        }
        return;
      }
      const cur = g.current;
      if (!cur || locked) return;
      const dx = ev.clientX - cur.sx;
      const dy = ev.clientY - cur.sy;
      if (!cur.moved && Math.hypot(dx, dy) < TAP_PX) return;
      cur.moved = true;
      const card = cardRect();
      if (!card?.width || !card?.height) return;
      if (cur.key === 'media') {
        const lim = Math.min(100, 50 * mediaScale());
        onFrame?.(clampFrame({ ...cur.p0, x: clamp(cur.p0.x + (dx / card.width) * 100, -lim, lim), y: clamp(cur.p0.y + (dy / card.height) * 100, -lim, lim) }));
        setPanned(true);
        setDragging('media');
        return;
      }
      const r = cur.el.getBoundingClientRect();
      const wP = (r.width / card.width) * 100;
      const hP = (r.height / card.height) * 100;
      onLayout({
        ...L,
        [cur.key]: {
          x: clamp(cur.p0.x + (dx / card.width) * 100, 0, Math.max(0, 100 - wP)),
          y: clamp(cur.p0.y + (dy / card.height) * 100, TOP_LIMIT, Math.max(TOP_LIMIT, SAFE_TOP - hP)),
        },
      });
      setDragging((d) => (d === cur.key ? d : cur.key));
    };
    const onUp = (ev) => {
      const id = ev.pointerId ?? 1;
      if (!ptrs.current.has(id)) return;
      ptrs.current.delete(id);
      if (ptrs.current.size < 2) pinch.current = null;
      if (ptrs.current.size) return;
      const cur = g.current;
      g.current = null;
      setDragging(null);
      if (!cur || cur.moved || cur.key === 'media') return;
      if (cur.key === 'text') edit();
      else if (!locked) onSelected?.(cur.key);
    };
    window.addEventListener('pointerdown', onWinDown, true);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointerdown', onWinDown, true);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [onLayout, onFrame, onSelected, edit, locked, startPinch]);

  // ---- the rotate handle ----
  const rotStart = (key) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    const box = e.currentTarget.parentElement.getBoundingClientRect();
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    const a0 = Math.atan2(e.clientY - cy, e.clientX - cx);
    const r0 = rotOf(key);
    const move = (ev) => {
      const L = layoutRef.current;
      onLayout({ ...L, rot: { ...L.rot, [key]: snapRotation(r0 + ((Math.atan2(ev.clientY - cy, ev.clientX - cx) - a0) * 180) / Math.PI) } });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDragging(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    setDragging(key);
  };

  // ---- the size wedge: 72 at the top, 18 at the bottom ----
  const dragSize = (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const at = (ev) => {
      const t = clamp((ev.clientY - r.top) / r.height, 0, 1);
      set({ size: Math.round(SIZE_MAX - t * SIZE_RANGE) });
    };
    at(e);
    const up = () => {
      window.removeEventListener('pointermove', at);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDragging(null);
    };
    window.addEventListener('pointermove', at);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    setDragging('size');
  };

  const wheelSize = (e) => {
    if (!e.deltaY || locked) return;
    e.preventDefault();
    const d = e.ctrlKey ? -e.deltaY * 0.25 : (e.deltaY < 0 ? 1 : -1);
    set({ size: Math.round(clamp(layoutRef.current.size + d, SIZE_MIN, SIZE_MAX)) });
  };

  const wheelMedia = (e) => {
    if (!onFrame || locked || !e.deltaY) return;
    e.preventDefault();
    const d = e.ctrlKey ? -e.deltaY * 0.01 : (e.deltaY < 0 ? 0.05 : -0.05);
    onFrame(clampFrame({ ...(frameRef.current || FRAME_DEFAULT), scale: clamp(mediaScale() + d, MEDIA_SCALE_MIN, MEDIA_SCALE_MAX) * 100 }));
    setPanned(true);
  };

  // ---- the base layer: done / deselect / pan the picture / swipe the background ----
  const downBase = (e) => {
    if (e.button != null && e.button !== 0) return;
    if (editingRef.current) { done(); return; }
    if (selectedRef.current) { onSelected?.(null); return; }
    if (locked) return;
    if (onFrame) { down('media')(e); return; }
    if (textOnly) {
      const x0 = e.clientX;
      const up = (ev) => {
        window.removeEventListener('pointerup', up);
        const dx = ev.clientX - x0;
        if (Math.abs(dx) < SWIPE_PX) return;
        pickBackground(cycle(BACKGROUNDS, layoutRef.current.background, dx < 0 ? 1 : -1));
      };
      window.addEventListener('pointerup', up);
    }
  };

  // ---- keep every element out of the guests' row ----
  useLayoutEffect(() => {
    const card = cardRect();
    if (!card?.height) return;
    const patch = {};
    ['text', 'event', 'venue'].forEach((key) => {
      const el = elOf(key);
      if (!el) return;
      if (key === 'event' && !event) return;
      if (key === 'venue' && !venue) return;
      const hP = (el.getBoundingClientRect().height / card.height) * 100;
      const maxY = Math.max(TOP_LIMIT, SAFE_TOP - hP);
      const p = layout[key];
      if (p.y > maxY + 0.05) patch[key] = { ...p, y: maxY };
    });
    if (Object.keys(patch).length) onLayout({ ...layout, ...patch });
    // Re-measured whenever a block can change height.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, chip, layout.size, layout.font, layout.highlight, layout.eventStyle, layout.venueStyle, layout.scl?.event, layout.scl?.venue, layout.rot?.text, layout.rot?.event, layout.rot?.venue, scale, !!event, !!venue]);

  // ---- the background: a swatch, or a swipe across the card ----
  const pickBackground = (key) => {
    set({ background: key });
    const row = rowRef.current;
    const i = BACKGROUNDS.findIndex((b) => b.key === key);
    if (row?.children[i]) row.scrollTo?.({ left: Math.max(0, row.children[i].offsetLeft - row.clientWidth / 2 + 20), behavior: 'smooth' });
  };

  const reset = () => {
    onLayout({ ...layoutRef.current, size: SIZE_DEFAULT, text: { ...TEXT_DEFAULT }, event: { ...EVENT_DEFAULT }, venue: { ...VENUE_DEFAULT }, rot: { ...ROT_DEFAULT }, scl: { ...SCL_DEFAULT } });
    onFrame?.({ ...FRAME_DEFAULT });
    setPanned(false);
  };

  // The tooltip stays inside the card: measured after each render, shifted sideways when it
  // would cross an edge, its arrow moved the other way so it keeps pointing at the lock.
  useLayoutEffect(() => {
    const el = tipRef.current;
    const box = boxRef.current;
    if (!el || !box) return;
    el.style.setProperty('--dx', '0px');
    const r = el.getBoundingClientRect();
    const c = box.getBoundingClientRect();
    if (!c.width) return;
    const dx = r.left < c.left + 8 ? c.left + 8 - r.left : r.right > c.right - 8 ? c.right - 8 - r.right : 0;
    el.style.setProperty('--dx', `${Math.round(dx)}px`);
  });

  const hl = highlightOf(layout.highlight);
  const knobY = ((SIZE_MAX - layout.size) / SIZE_RANGE) * 100;
  const tipOn = !!venueTip && !!venue && !venueOpen && showChrome && !locked && !editing && dragging !== 'venue';
  const interactive = !locked || !!onTapText;
  const chromeOn = showChrome && !locked;
  const railOn = chromeOn && showRail && !editing && !tipOn;   // the tooltip is the one thing on screen while it asks
  const styleBarOn = chromeOn && showStyleBar && !editing && !!selected && ((selected === 'event' && !!event) || (selected === 'venue' && !!venue));
  const swatchesOn = chromeOn && showSwatches && textOnly && !editing && !selected;
  const hintOn = chromeOn && !!onFrame && !editing && !selected && !panned;
  const showSafe = !!dragging && ['text', 'event', 'venue'].includes(dragging);

  const railItems = [
    { key: 'text', label: 'TEXT', on: false, go: edit, icon: <span style={{ fontFamily: "'Times New Roman', Times, serif", fontStyle: 'italic', fontSize: 19, lineHeight: 1 }}>Aa</span> },
    { key: 'event', label: 'EVENT', on: !!event, go: () => onOpen?.('event'), icon: <TicketIcon size={19} w={2} /> },
    { key: 'venue', label: 'VENUE', on: !!venue, go: () => onOpen?.('venue'), icon: <PinIcon size={19} /> },
    { key: 'window', label: 'WINDOW', on: windowOn, go: () => onOpen?.('window'), icon: <ClockIcon /> },
    ...(isVideo ? [{ key: 'trim', label: 'TRIM', on: false, go: () => onOpen?.('trim'), icon: <ScissorsIcon /> }] : []),
    { key: 'reset', label: 'RESET', on: false, go: reset, icon: <ResetIcon /> },
  ].filter((b) => rail.includes(b.key));

  return (
    <div ref={boxRef} className="absolute inset-0 font-mono text-white" style={{ zIndex: 3, userSelect: 'none', pointerEvents: 'none' }} data-testid="portal-layout-editor">
      {/* The base layer: under the elements, over the picture. */}
      <div
        aria-hidden
        data-testid="canvas-base"
        onPointerDown={downBase}
        onWheel={wheelMedia}
        style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'auto', touchAction: onFrame && !locked ? 'none' : 'pan-y', cursor: onFrame && !locked ? (dragging === 'media' ? 'grabbing' : 'grab') : 'default' }}
      />

      {showSafe && (
        <div aria-hidden data-testid="safe-zone" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${100 - SAFE_TOP}%`, zIndex: 3, background: 'repeating-linear-gradient(135deg, rgba(0,0,0,.55) 0 8px, rgba(0,0,0,.3) 8px 16px)', borderTop: '2px dashed rgba(0,255,255,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', background: 'rgba(0,0,0,.72)', padding: '5px 12px', borderRadius: 999 }}>I’M DOWN + REPLY SIT HERE</span>
        </div>
      )}

      <PortalWords
        ref={wordsRef}
        text={text}
        chip={chip}
        layout={layout}
        scale={scale}
        interactive={interactive}
        editable
        editing={editing}
        spanRef={spanRef}
        onInput={onText}
        dragging={dragging === 'text'}
        onPointerDown={interactive ? down('text') : null}
        onWheel={wheelSize}
      >
        {editing && <RotateHandle onPointerDown={rotStart('text')} />}
      </PortalWords>

      {event && (
        <PortalEvent
          ref={eventRef}
          event={event}
          layout={layout}
          linkTo={null}
          interactive={!locked}
          dragging={dragging === 'event'}
          selected={selected === 'event'}
          onPointerDown={locked ? null : down('event')}
        >
          {selected === 'event' && !locked && <RotateHandle onPointerDown={rotStart('event')} />}
        </PortalEvent>
      )}
      {venue && (
        <PortalVenue
          ref={venueRef}
          venue={venue}
          date={venueDate}
          layout={layout}
          interactive={!locked}
          dragging={dragging === 'venue'}
          selected={selected === 'venue'}
          onPointerDown={locked ? null : down('venue')}
        >
          {selected === 'venue' && !locked && !tipOn && <RotateHandle onPointerDown={rotStart('venue')} />}
          {!venueOpen && !locked && onVenueOpen && (
            <span aria-label="Shown once they're in" data-testid="venue-lock-badge" style={{ position: 'absolute', right: -2, top: -2, zIndex: 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.6)', pointerEvents: 'none' }}>
              <LockIcon />
            </span>
          )}
          {tipOn && (
            <div
              ref={tipRef}
              role="dialog"
              aria-label="Venue privacy"
              data-testid="venue-tip"
              className="prc-motion font-mono"
              onPointerDown={(e) => e.stopPropagation()}
              style={{ position: 'absolute', right: -10, bottom: 'calc(100% + 12px)', zIndex: 6, width: 256, display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 14px 12px', background: '#fff', color: '#000', borderRadius: 22, boxShadow: '0 8px 28px rgba(0,0,0,.55)', transform: 'translateX(var(--dx, 0px))', cursor: 'default', animation: 'prcTipIn .32s cubic-bezier(.2,.7,.2,1) both', pointerEvents: 'auto' }}
            >
              <span aria-hidden style={{ position: 'absolute', right: 'calc(14px + var(--dx, 0px))', bottom: -6, width: 14, height: 14, background: '#fff', transform: 'rotate(45deg)', borderRadius: '0 0 3px 0' }} />
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', whiteSpace: 'nowrap' }}>
                <span style={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LockIcon /></span>
                LOCKED UNTIL YOU LET THEM IN
              </span>
              <span style={{ fontSize: 12, lineHeight: 1.45 }}>{mode === 'curated' ? 'Guests see the place once you let them in. Want it showing from the start?' : 'Guests see the place once they tap I’m down. Want it showing from the start?'}</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 6 }}>
                <button type="button" onClick={() => onVenueTip?.(false)} style={{ height: 34, background: '#000', color: '#fff', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer' }}>KEEP LOCKED</button>
                <button type="button" onClick={() => { onVenueOpen?.(true); onVenueTip?.(false); }} className="prc-pill" style={{ height: 34, background: 'rgba(0,0,0,.08)', color: '#000', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer' }}>SHOW IT NOW</button>
              </div>
            </div>
          )}
        </PortalVenue>
      )}

      {/* ---- typing: DONE, the size wedge, HIGHLIGHT, the font row ---- */}
      {chromeOn && editing && (
        <>
          <button
            type="button"
            onClick={done}
            onPointerDown={(e) => e.preventDefault()}
            className="prc-ctl"
            style={{ position: 'absolute', right: 16, top: 16, zIndex: 9, height: 40, padding: '0 20px', background: '#fff', color: '#000', border: 0, borderRadius: 999, fontSize: 13, fontWeight: 700, letterSpacing: '0.14em', pointerEvents: 'auto' }}
          >
            DONE
          </button>
          <div
            role="slider"
            aria-label="Text size"
            aria-valuemin={SIZE_MIN}
            aria-valuemax={SIZE_MAX}
            aria-valuenow={layout.size}
            tabIndex={0}
            onPointerDown={dragSize}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp') set({ size: clamp(layout.size + 1, SIZE_MIN, SIZE_MAX) });
              if (e.key === 'ArrowDown') set({ size: clamp(layout.size - 1, SIZE_MIN, SIZE_MAX) });
            }}
            style={{ position: 'absolute', left: 4, top: '17%', height: '36%', width: 30, zIndex: 9, cursor: 'ns-resize', touchAction: 'none', pointerEvents: 'auto' }}
            data-testid="size-slider"
          >
            <div aria-hidden style={{ position: 'absolute', top: 0, left: '50%', width: 0, height: '100%', transform: 'translateX(-50%)', borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: '295px solid rgba(255,255,255,.55)', boxSizing: 'border-box', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', left: '50%', top: `${knobY}%`, width: 20, height: 20, transform: 'translate(-50%,-50%)', background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,.6)', pointerEvents: 'none' }} />
          </div>
          <div style={{ position: 'absolute', right: 12, top: 86, zIndex: 9, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, pointerEvents: 'auto' }}>
            <button
              type="button"
              aria-label="Highlight"
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => set({ highlight: cycle(HIGHLIGHTS, layout.highlight) })}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, padding: 0, background: 'transparent', border: 0, color: '#fff', fontFamily: 'inherit', cursor: 'pointer' }}
            >
              <span className="prc-pill" style={circle(false)}><HighlightIcon /></span>
              <span style={label9}>{hl.label}</span>
            </button>
          </div>
          <div
            className="prc-style-bar"
            data-testid="text-style-bar"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: kbInset,
              zIndex: 9,
              padding: '14px 0 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              background: 'linear-gradient(to top, rgba(0,0,0,.85), rgba(0,0,0,0))',
              pointerEvents: 'auto',
            }}
          >
            <div style={{ padding: '0 16px', display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 10, letterSpacing: '0.06em', color: 'rgba(255,255,255,.75)' }}>
              <span>No page, no price. It expires on its own.</span>
              <span style={{ fontWeight: 700 }} data-testid="words-count">{(text || '').length}/{TEXT_MAX}</span>
            </div>
            <div className="prc-row flex" style={{ gap: 8, overflowX: 'auto', padding: '2px 16px' }} data-testid="font-row">
              {FONTS.map((f) => {
                const on = f.key === layout.font;
                return (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={on}
                    aria-label={`Font ${f.label}`}
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => set({ font: f.key })}
                    style={{ flex: '0 0 auto', height: 40, padding: '0 18px', background: on ? '#fff' : 'rgba(0,0,0,.72)', color: on ? '#000' : '#fff', border: 0, borderRadius: 999, fontFamily: f.family, fontStyle: f.style, fontWeight: f.weight, fontSize: 16, lineHeight: 1, whiteSpace: 'nowrap', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ---- the right rail ---- */}
      {railOn && (
        <div style={{ position: 'absolute', right: 12, top: railTop, zIndex: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, pointerEvents: 'auto' }} data-testid="rail">
          {railItems.map((b) => (
            <button key={b.key} type="button" aria-label={b.label} onClick={b.go} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 0, background: 'transparent', border: 0, color: '#fff', fontFamily: 'inherit', cursor: 'pointer' }}>
              <span style={{ position: 'relative', display: 'block', width: 44, height: 44 }}>
                {b.on && <span aria-hidden data-testid={`rail-ring-${b.key}`} style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: 'prcSpin 6s linear infinite' }} />}
                <span className="prc-pill" style={{ ...circle(false), position: 'absolute', inset: b.on ? 3 : 0, width: 'auto', height: 'auto' }}>{b.icon}</span>
              </span>
              <span style={label9}>{b.label}</span>
            </button>
          ))}
        </div>
      )}

      {hintOn && (
        <span data-testid="frame-hint" style={{ position: 'absolute', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 6, pointerEvents: 'none', whiteSpace: 'nowrap', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', background: 'rgba(0,0,0,.72)', padding: '8px 14px', borderRadius: 999 }}>
          DRAG TO MOVE · PINCH TO SCALE AND TURN
        </span>
      )}

      {swatchesOn && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 6, padding: safePad('16px'), background: 'linear-gradient(to top, rgba(0,0,0,.85), rgba(0,0,0,0))', pointerEvents: 'auto' }}>
          <BackgroundSwatches value={layout.background} onChange={pickBackground} rowRef={rowRef} />
        </div>
      )}

      {styleBarOn && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 9, padding: safePad('12px 12px 16px'), background: 'linear-gradient(to top, rgba(0,0,0,.9), rgba(0,0,0,0))', pointerEvents: 'auto' }}>
          <ElementStyleBar
            kind={selected}
            value={selected === 'event' ? layout.eventStyle : layout.venueStyle}
            onChange={(k) => set(selected === 'event' ? { eventStyle: k } : { venueStyle: k })}
            onSearch={() => { onSelected?.(null); onOpen?.(selected); }}
            onRemove={() => { const k = selected; onSelected?.(null); onRemove?.(k); }}
            venueOpen={venueOpen}
            onVenueOpen={onVenueOpen}
          />
        </div>
      )}
    </div>
  );
}
